import { useEffect, useRef } from 'react';
import notificationAPI from '../api/notifications';
import logger from '../utils/logger';

const DISPATCH_THROTTLE_MS = 5 * 60 * 1000; // 5 min minimum between dispatches

/**
 * Background hook that delivers certified OI escalations as server-side notifications.
 *
 * Accepts the `escalatedItems` array from `useEscalationCounts` (same React Query cache,
 * no extra network requests). Serialises the current escalation state and POSTs to
 * `POST /api/notifications/alerts/oi-digest` when:
 *   (a) the set of escalated entities or their levels changes, OR
 *   (b) the throttle window has expired since the last dispatch.
 *
 * The backend owns deduplication (4h cooldown per entity+level) and delivery.
 * This hook never throws and produces no visible UI side-effects.
 *
 * @param {Array} escalatedItems - From useEscalationCounts().escalatedItems
 */
export function useOIAlertDispatch(escalatedItems) {
  const lastDispatch = useRef({ serialized: '', time: 0 });

  useEffect(() => {
    if (!escalatedItems || escalatedItems.length === 0) return;

    const serialized = escalatedItems
      .map(i => `${i.entityId}:${i.level}`)
      .sort()
      .join(',');

    const now = Date.now();
    const { serialized: lastSerial, time: lastTime } = lastDispatch.current;

    const stateChanged = serialized !== lastSerial;
    const throttleExpired = now - lastTime > DISPATCH_THROTTLE_MS;

    // Dispatch only when something changed or throttle window reset
    if (!stateChanged && !throttleExpired) return;

    lastDispatch.current = { serialized, time: now };

    notificationAPI
      .postOIDigest({ escalations: escalatedItems })
      .catch(err => {
        logger.debug('OI alert dispatch (non-critical): %o', err);
      });
  }, [escalatedItems]);
  // Note: lastDispatch ref is intentionally excluded — it is a ref, not reactive state.
}
